import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Brain, Menu, X, Target, Code, Cloud, Database, Shield, Lock, Bot, Sparkles, Eye, Mic, TrendingUp, Scale, GraduationCap, ChevronDown, ChevronUp, Blocks, FileText, Heart, Cog, BookOpen, Handshake, BarChart3, Utensils } from 'lucide-react';
import { ServicesDropdown } from './ServicesDropdown';
import { ResourcesDropdown } from './ResourcesDropdown';
import { ThemeToggle } from './ThemeToggle';
import { useBanner } from '../contexts/BannerContext';

interface HeaderProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
}

export const Header: React.FC<HeaderProps> = ({
  isMenuOpen,
  setIsMenuOpen,
}) => {
  const location = useLocation();
  const { isBannerVisible, bannerHeight } = useBanner();
  const [isServicesExpanded, setIsServicesExpanded] = useState(false);
  const [isResourcesExpanded, setIsResourcesExpanded] = useState(false);
  
  // Prevent body scroll when menu is open and reset submenu state
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '0px'; // Prevent layout shift
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '';
      setIsServicesExpanded(false); // Reset services submenu when main menu closes
      setIsResourcesExpanded(false); // Reset resources submenu when main menu closes
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '';
    };
  }, [isMenuOpen]);
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const services = [
    { icon: <Target className="w-4 h-4" />, title: "Cognitive Reach", href: "/services/cognitive-reach" },
    { icon: <GraduationCap className="w-4 h-4" />, title: "Cognitive Train", href: "/services/cognitive-train" },
    { icon: <Cloud className="w-4 h-4" />, title: "Cognitive Cloud", href: "/services/cognitive-cloud" },
    { icon: <Code className="w-4 h-4" />, title: "Cognitive Build", href: "/services/cognitive-build" },
    { icon: <Brain className="w-4 h-4" />, title: "Cognitive Intelligence", href: "/services/cognitive-intelligence" },
    { icon: <Bot className="w-4 h-4" />, title: "Cognitive Agents", href: "/services/cognitive-agents" },
    { icon: <Sparkles className="w-4 h-4" />, title: "Cognitive Generate", href: "/services/cognitive-generate" },
    { icon: <Eye className="w-4 h-4" />, title: "Cognitive Vision", href: "/services/cognitive-vision" },
    { icon: <Mic className="w-4 h-4" />, title: "Cognitive Voice", href: "/services/cognitive-voice" },
    { icon: <TrendingUp className="w-4 h-4" />, title: "Cognitive Predict", href: "/services/cognitive-predict" },
    { icon: <Database className="w-4 h-4" />, title: "Cognitive IoT", href: "/services/cognitive-iot" },
    { icon: <Lock className="w-4 h-4" />, title: "Cognitive Secure", href: "/services/cognitive-secure" },
    { icon: <Utensils className="w-4 h-4" />, title: "Cognitive Robotics", href: "/services/cognitive-robotics" },
    { icon: <Scale className="w-4 h-4" />, title: "Cognitive Compliance", href: "/services/cognitive-compliance" },
    { icon: <Blocks className="w-4 h-4" />, title: "Cognitive Ledger", href: "/services/cognitive-ledger" }
  ];

  const resources = {
    industries: [
      { icon: <Heart className="w-4 h-4" />, title: "Healthcare AI", href: "/industries/healthcare" },
      { icon: <Cog className="w-4 h-4" />, title: "Manufacturing AI", href: "/industries/manufacturing" }
    ],
    blogs: [
      { icon: <BookOpen className="w-4 h-4" />, title: "AI Implementation Guide", href: "/blog/ai-implementation-guide" },
      { icon: <FileText className="w-4 h-4" />, title: "All Blog Posts", href: "/blog" }
    ],
    business: [
      { icon: <BarChart3 className="w-4 h-4" />, title: "Case Studies", href: "/case-studies" },
      { icon: <TrendingUp className="w-4 h-4" />, title: "Investors", href: "/investors" },
      { icon: <Handshake className="w-4 h-4" />, title: "Partnerships", href: "/partnerships" }
    ]
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    if (location.pathname === '/') {
      e.preventDefault();
      scrollToTop();
    }
    // If not on home page, let the Link navigate to home and scroll will happen automatically when page loads
  };

  return (
    <header>
      <nav
        className="fixed w-full z-40 bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border-b border-gray-200 dark:border-blue-500/20 shadow-lg dark:shadow-2xl px-4 sm:px-6 lg:px-8"
        style={{ top: isBannerVisible ? `${bannerHeight}px` : '0' }}
        role="navigation"
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" onClick={handleLogoClick} className="flex items-center space-x-2 sm:space-x-3 cursor-pointer group">
              <div className="relative">
                <img 
                  src="https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/3c9c5ef8-4fb8-4601-7c68-b81fc7e6a000/public"
                  alt="Cognitive Craft Hub Logo"
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl shadow-lg group-hover:shadow-blue-600/50 transition-all duration-300 group-hover:scale-110"
                />
              </div>
              <span className="hidden sm:inline text-lg sm:text-xl font-bold text-gray-900 dark:bg-gradient-to-r dark:from-blue-400 dark:to-blue-400 dark:bg-clip-text dark:text-transparent leading-normal">
                Cognitive Craft Hub
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link
                to="/about"
                className={`relative font-medium transition-all duration-300 text-gray-700 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 ${
                  location.pathname === "/about" ? "text-blue-600 dark:text-blue-400" : ""
                }`}
              >
                About
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 dark:bg-blue-400 transition-all duration-300 hover:w-full"></span>
              </Link>
              <ServicesDropdown />
              <ResourcesDropdown />
              <Link
                to="/careers"
                className={`relative font-medium transition-all duration-300 text-gray-700 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 ${
                  location.pathname === "/careers" ? "text-blue-600 dark:text-blue-400" : ""
                }`}
              >
                Careers
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 dark:bg-blue-400 transition-all duration-300 hover:w-full"></span>
              </Link>
              <Link
                to="/contact"
                className={`relative font-medium transition-all duration-300 text-gray-700 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 ${
                  location.pathname === "/contact" ? "text-blue-600 dark:text-blue-400" : ""
                }`}
              >
                Contact
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 dark:bg-blue-400 transition-all duration-300 hover:w-full"></span>
              </Link>
            </div>
            
            {/* Desktop Navigation & Theme Toggle */}
            <div className="hidden lg:flex items-center space-x-4">
              <ThemeToggle />
            </div>

            {/* Mobile Controls */}
            <div className="flex items-center space-x-3 lg:hidden">
              <ThemeToggle />
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                )}
              </button>
            </div>
          </div>
        </div>

        {isMenuOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/30 z-50" 
            style={{ 
              top: isBannerVisible ? `${bannerHeight + 25}px` : '64px',
              height: isBannerVisible ? `calc(100vh - ${bannerHeight + 64}px)` : 'calc(100vh - 64px)'
            }}
          >
            <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border-t border-gray-200 dark:border-blue-500/20 w-full h-full overflow-y-auto p-6 shadow-2xl">
              <div className="space-y-6">
                <div className="space-y-3">
                  <Link
                    to="/about"
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-4 py-3 text-gray-700 dark:text-white hover:bg-blue-100 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-xl font-medium transition-all duration-200"
                  >
                    About
                  </Link>
                  {/* Services Submenu */}
                  <div>
                    <button
                      onClick={() => setIsServicesExpanded(!isServicesExpanded)}
                      className="w-full flex items-center justify-between px-4 py-3 text-gray-700 dark:text-white hover:bg-blue-100 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-xl font-medium transition-all duration-200"
                    >
                      <span>Services</span>
                      {isServicesExpanded ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                    
                    {/* Services Submenu Items */}
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isServicesExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      <div className="ml-4 mt-2 space-y-1 pb-4">
                        {services.map((service, index) => (
                          <Link
                            key={index}
                            to={service.href}
                            onClick={() => setIsMenuOpen(false)}
                            className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg transition-all duration-200"
                          >
                            <span className="text-blue-600 dark:text-blue-400 mr-2">{service.icon}</span>
                            {service.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Resources Submenu */}
                  <div>
                    <button
                      onClick={() => setIsResourcesExpanded(!isResourcesExpanded)}
                      className="w-full flex items-center justify-between px-4 py-3 text-gray-700 dark:text-white hover:bg-blue-100 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-xl font-medium transition-all duration-200"
                    >
                      <span>Resources</span>
                      {isResourcesExpanded ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                    
                    {/* Resources Submenu Items */}
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isResourcesExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      <div className="ml-4 mt-2 space-y-1 pb-4">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Industry Solutions
                        </div>
                        {resources.industries.map((item, index) => (
                          <Link
                            key={index}
                            to={item.href}
                            onClick={() => setIsMenuOpen(false)}
                            className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg transition-all duration-200"
                          >
                            <span className="text-blue-600 dark:text-blue-400 mr-2">{item.icon}</span>
                            {item.title}
                          </Link>
                        ))}
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-3">
                          Learning Resources
                        </div>
                        {resources.blogs.map((item, index) => (
                          <Link
                            key={index}
                            to={item.href}
                            onClick={() => setIsMenuOpen(false)}
                            className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg transition-all duration-200"
                          >
                            <span className="text-blue-600 dark:text-blue-400 mr-2">{item.icon}</span>
                            {item.title}
                          </Link>
                        ))}
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-3">
                          Business Resources
                        </div>
                        {resources.business.map((item, index) => (
                          <Link
                            key={index}
                            to={item.href}
                            onClick={() => setIsMenuOpen(false)}
                            className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg transition-all duration-200"
                          >
                            <span className="text-blue-600 dark:text-blue-400 mr-2">{item.icon}</span>
                            {item.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                  <Link
                    to="/careers"
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-4 py-3 text-gray-700 dark:text-white hover:bg-blue-100 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-xl font-medium transition-all duration-200"
                  >
                    Careers
                  </Link>
                  <Link
                    to="/faq"
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-4 py-3 text-gray-700 dark:text-white hover:bg-blue-100 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-xl font-medium transition-all duration-200"
                  >
                    FAQ
                  </Link>
                  
                  <div className="pt-4 mt-4 border-t border-blue-200 dark:border-blue-500/20">
                    <Link
                      to="/contact"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                    >
                      Contact Us
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};